import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';

export interface AiModelRatioDatum {
  /** Stable AI model id (string form). */
  id: string;
  /** Display name. */
  label: string;
  /** Owner-share ratio as a percentage (0–100). */
  percent: number;
}

interface AiModelRatioChartProps {
  data: AiModelRatioDatum[];
  loading?: boolean;
  /** Pixel height of the bar plotting area. */
  height?: number;
}

const fmtPercent = (p: number) => `${p.toFixed(2)}%`;

/**
 * Lightweight per-AI-model bar chart: a row of headline stat cards above a
 * column of bars whose height is proportional to each model's owner-share
 * percentage. Built with plain MUI primitives (no charting dependency).
 */
export function AiModelRatioChart({ data, loading, height = 260 }: AiModelRatioChartProps) {
  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (data.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          No data for the selected filters.
        </Typography>
      </Paper>
    );
  }

  // Scale bars against the largest value (min 1% so a single low bar is still
  // visible), capped at 100%.
  const maxPercent = Math.max(...data.map((d) => d.percent), 1);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      {/* Headline stat cards. */}
      <Stack direction="row" flexWrap="wrap" spacing={4} sx={{ mb: 3 }}>
        {data.map((d) => (
          <Box key={`stat-${d.id}`} sx={{ minWidth: 120 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {d.label}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {fmtPercent(d.percent)}
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* Bars. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 4,
          height,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2,
        }}
      >
        {data.map((d) => (
          <Box
            key={`bar-${d.id}`}
            sx={{
              flex: 1,
              minWidth: 80,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              {fmtPercent(d.percent)}
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 140,
                height: `${(d.percent / maxPercent) * 100}%`,
                minHeight: 2,
                bgcolor: 'primary.main',
                borderRadius: '6px 6px 0 0',
              }}
            />
          </Box>
        ))}
      </Box>

      {/* X-axis labels, aligned under each bar. */}
      <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
        {data.map((d) => (
          <Typography
            key={`label-${d.id}`}
            variant="caption"
            sx={{ flex: 1, minWidth: 80, textAlign: 'center' }}
            noWrap
          >
            {d.label}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
}
